import React, { Fragment } from 'react'
import ButtonV2 from 'Components/Buttons/ButtonV2'
import EditIcon from '../../icons/EditIcon'
import TableTotals from '../../../../../Components/_newComponents/TableParts/TableTotals/TableTotals'
import InputV2 from '../../../../../Components/_newComponents/InputV2'
import { headers } from './options'
import TableHeader from './components/TableHeader'
import TableRow from './components/TableRow'
import ModalV2 from 'Components/ModalV2'
import { useStepResultsLogic } from './hooks/useStepResultsLogic'
import { useStepResultsStats } from './hooks/useStepResultsStats'
import { styles } from './styles'
import TooltipWrapper from '../../../../../Components/_newComponents/TooltipWrapper'

const StepResults = ({
  city,
  classes,
  runSnackBar,
  data,
  locations,
  cvrPercentiles,
  setData,
  isEdit,
  setIsEdit,
  setModalProps,
  campaignId,
  skippedLocations,
}) => {
  const logic = useStepResultsLogic(
    data,
    runSnackBar,
    city,
    setData,
    isEdit,
    setIsEdit,
    setModalProps,
    campaignId,
  )
  const stats = useStepResultsStats(logic.currentData)

  const {
    openedRow,
    baPerMission,
    payAdjustment,
    modalProps,
    currentData,
    handleRowOpen,
    handleBaPerMissionChange,
    handlePayAdjustmentChange,
    handleCancel,
    handleSave,
    handleApplyGlobalBaPerMission,
    handleLocationChange,
    handleMissionBaChange,
    handleMissionTimeChange,
    handleMissionRatingChange,
    handleMissionFeedbackChange,
    requiredRatings,
    isRatingThresholdMet,
    onClickDeleteDay,
    onClickDeleteLocation,
  } = logic

  const {
    tableFooterData,
    uniqMissions,
    totalMissions,
    weatherConditionsHashMap,
    badWeatherInADay,
  } = stats

  const currency = city.country.currencySign

  return (
    <div className={classes.pageContent}>
      <div style={styles.titleWrapper}>
        <div>
          <div className={classes.contentTitleMain}>Results</div>
          {isEdit ? (
            <div className={classes.contentDescription}>
              Review the missions created based on your selections. Make final
              adjustments before launching.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className={classes.contentDescription}>
                Review the missions created based on your selections. Make final
                adjustments before launching.
              </div>
              <div className={classes.contentDescription}>
                Please{' '}
                <span
                  style={{
                    fontWeight: 700,
                    color: !isRatingThresholdMet ? 'red' : '#011533',
                  }}
                >
                  rate at least {requiredRatings} missions
                </span>{' '}
                to help the AI improve, then make any final edits before
                confirming.
              </div>
              {skippedLocations.length > 0 && !isEdit ? (
                <TooltipWrapper
                  tooltipComponent={
                    <div
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                        {skippedLocations.map(name => (
                          <li key={name} style={{ fontSize: 12 }}>
                            {name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  }
                >
                  <div className={classes.warningBox}>
                    <span>
                      {skippedLocations.length === 1
                        ? '1 whitelisted location was skipped due to no matches with its defined schedule and the selected timeslots. Hover for details.'
                        : `${
                            skippedLocations.length
                          } whitelisted locations were skipped due to no matches with their defined schedules and the selected timeslots. Hover for details.`}
                    </span>
                  </div>
                </TooltipWrapper>
              ) : null}
            </div>
          )}
        </div>
        <div style={styles.buttonSection}>
          {!isEdit ? (
            <ButtonV2
              label={
                <div style={styles.buttonLabel}>
                  <EditIcon style={styles.icon} /> EDIT
                </div>
              }
              onClick={() => setIsEdit(true)}
              style={{}}
            />
          ) : (
            <Fragment>
              <div style={styles.buttonContainer}>
                <ButtonV2
                  label="Cancel"
                  outlined={false}
                  onClick={handleCancel}
                  styles={{ width: '146px' }}
                />
                <ButtonV2
                  label="Save"
                  onClick={handleSave}
                  styles={{ width: '146px' }}
                />
              </div>
              <div style={styles.editHint}>
                Your changes will be applied into the calculations after you
                save them.
              </div>
            </Fragment>
          )}
        </div>
      </div>
      <div
        style={{
          ...styles.totalHeaderWrapper,
          marginTop: isEdit ? '0px' : '44px',
        }}
      >
        <div style={styles.totalHeaderItems}>
          <InputV2
            value={payAdjustment}
            label="Pay Adjustment:"
            viewOnly={!isEdit}
            onChange={handlePayAdjustmentChange}
            prefix={currency}
            width="100px"
            type="float"
            max={99999}
          />

          <InputV2
            value={baPerMission}
            label="BAs per mission:"
            viewOnly={!isEdit}
            onChange={handleBaPerMissionChange}
            type="int"
            max={99}
            tooltip="The amount of BAs will be changed for all the missions according to your input. You can change it for a specific mission from the table below."
          />
          {isEdit ? (
            <ButtonV2
              label="Apply"
              outlined={false}
              onClick={handleApplyGlobalBaPerMission}
              disabled={!baPerMission}
              styles={{ marginLeft: '-28px', height: '32px' }}
            />
          ) : null}
        </div>
        {!isEdit ? (
          <div style={styles.totalHeaderItems}>
            <InputV2
              value={uniqMissions}
              label="Total Unique Missions:"
              viewOnly={true}
            />
            <InputV2
              value={totalMissions}
              label="Total Missions:"
              viewOnly={true}
              tooltip={
                <div>
                  <strong>Total missions</strong>
                  <br />
                  Total Unique Missions X BAs per mission = Total Missions
                </div>
              }
            />
          </div>
        ) : null}
      </div>
      <div>
        <div className={classes.contentTitle}>Selected Dates</div>
        <div
          className={classes.contentDescription}
          style={{ marginBottom: 10 }}
        >
          The dates and missions AI generated per time slot you selected are
          listed below.
        </div>
        <div>
          <TableHeader data={headers} />
          {currentData.map(item => (
            <TableRow
              key={item.id}
              data={item}
              onRowOpen={handleRowOpen}
              isOpened={item.id === openedRow}
              headers={headers}
              isEdit={isEdit}
              onClickDeleteDay={onClickDeleteDay}
              onClickDeleteLocation={onClickDeleteLocation}
              onLocationChange={handleLocationChange}
              onMissionBaChange={handleMissionBaChange}
              onMissionTimeChange={handleMissionTimeChange}
              onMissionRatingChange={handleMissionRatingChange}
              onMissionFeedbackChange={handleMissionFeedbackChange}
              locations={locations}
              cvrPercentiles={cvrPercentiles}
              weatherConditions={weatherConditionsHashMap[item.id]}
              isHaveBadWeather={badWeatherInADay[item.id]}
              cityId={city.id}
              countryCode={city.country.code}
            />
          ))}
          <TableTotals data={tableFooterData} />
        </div>
      </div>
      <ModalV2 {...modalProps} />
    </div>
  )
}

export default StepResults
